/**
 * Come raccontare a una persona cosa sta facendo un'automazione su un lead.
 *
 * Il motore parla per stati e id di passo; chi apre la scheda vuole sapere se
 * "Clara ci sta parlando", se "aspettiamo una risposta", o com'e' finita. Queste
 * funzioni sono pure, cosi' le frasi si provano senza interfaccia.
 */

export interface AutomazioneSulLead {
  id: string;
  status: string;
  entered_at: string;
  completed_at?: string | null;
  exit_reason?: string | null;
  trigger_data?: Record<string, unknown> | null;
  workflow?: { name?: string | null } | null;
  step?: { name?: string | null; step_type?: string | null; config?: Record<string, unknown> | null } | null;
}

export type Tono = 'in_corso' | 'attesa' | 'concluso' | 'fermo' | 'errore';

export interface Racconto {
  titolo: string;
  dettaglio: string;
  tono: Tono;
}

const ESITI: Record<string, string> = {
  appuntamento_fissato: 'Appuntamento fissato',
  non_interessato: 'Non interessato',
  silenzio: 'Non ha piu\' risposto',
  serve_una_persona: 'Serve una persona',
  success: 'Concluso bene',
  refused: 'Non interessato',
  no_response: 'Non ha piu\' risposto',
  error: 'Serve una persona',
};

export function descriviEsito(esito: unknown): string {
  if (typeof esito !== 'string' || !esito) return '';
  return ESITI[esito] ?? esito.replace(/_/g, ' ');
}

/** "da 5 minuti", "da 3 ore", "da 2 giorni", relativo a `adesso`. */
export function daQuanto(iso: string, adesso: Date = new Date()): string {
  const ms = adesso.getTime() - new Date(iso).getTime();
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 1) return 'da un istante';
  if (min < 60) return `da ${min} ${min === 1 ? 'minuto' : 'minuti'}`;
  const ore = Math.round(min / 60);
  if (ore < 24) return `da ${ore} ${ore === 1 ? 'ora' : 'ore'}`;
  const giorni = Math.round(ore / 24);
  return `da ${giorni} ${giorni === 1 ? 'giorno' : 'giorni'}`;
}

export function descriviAutomazione(a: AutomazioneSulLead, adesso: Date = new Date()): Racconto {
  const flusso = a.workflow?.name?.trim() || 'Automazione';
  const td = a.trigger_data ?? {};
  const tipoPasso = a.step?.step_type ?? '';
  const nomePasso = a.step?.name?.trim() || '';

  if (a.status === 'completed') {
    const esito = descriviEsito(td['_esito_conversazione']);
    return {
      titolo: esito ? `${flusso}: ${esito.toLowerCase()}` : `${flusso}: conclusa`,
      dettaglio: a.completed_at ? `Finita ${daQuanto(a.completed_at, adesso)}` : 'Finita',
      tono: 'concluso',
    };
  }
  if (a.status === 'failed') {
    return { titolo: `${flusso}: si e' fermata per un errore`, dettaglio: nomePasso ? `Sul passo "${nomePasso}"` : '', tono: 'errore' };
  }
  if (a.status === 'exited') {
    return {
      titolo: `${flusso}: interrotta`,
      dettaglio: a.exit_reason?.trim() || (a.completed_at ? daQuanto(a.completed_at, adesso) : ''),
      tono: 'fermo',
    };
  }

  // In corso: cosa sta succedendo adesso.
  const daQuando = daQuanto(a.entered_at, adesso);
  if (tipoPasso === 'agent_conversation') {
    const agente = String(a.step?.config?.['agent_name'] ?? '').trim() || "L'assistente AI";
    const turni = Number(td[`_turni_agente_${(a.step as { id?: string } | null)?.id ?? ''}`]) || 0;
    const inAscolto = td['_awaiting_reply'] === true;
    return {
      titolo: `${agente} sta seguendo la conversazione su WhatsApp`,
      dettaglio: `${inAscolto ? 'In attesa che il contatto scriva' : 'Sta rispondendo'} · ${turni ? `${turni} rispost${turni === 1 ? 'a' : 'e'} finora · ` : ''}nel flusso ${daQuando}`,
      tono: 'in_corso',
    };
  }
  if (tipoPasso === 'wait_for_reply') {
    return { titolo: 'In attesa che risponda su WhatsApp', dettaglio: `${flusso} · nel flusso ${daQuando}`, tono: 'attesa' };
  }
  if (tipoPasso === 'wait' || tipoPasso === 'delay_until') {
    return { titolo: 'In pausa programmata', dettaglio: `${flusso} · nel flusso ${daQuando}`, tono: 'attesa' };
  }
  return {
    titolo: `${flusso}: in corso`,
    dettaglio: `${nomePasso ? `Sul passo "${nomePasso}" · ` : ''}nel flusso ${daQuando}`,
    tono: 'in_corso',
  };
}
